<template>
  <section class="card">
    <h2>字典管理</h2>
    <p class="tip">
      维护系统字典类型与字典项。小程序展示文案请在本页维护类型
      <strong>MP_DISPLAY_GROUP</strong>（资讯展示分区）、<strong>MP_HOME_SECTION</strong>（首页区块标题）、
      <strong>MP_ADMIN_MENU</strong>（我的管理入口）；项编码为系统固定 key，显示名即小程序上看到的文字。
      <span v-if="canEdit">拖拽左侧「⋮⋮」可调整顺序，松手后自动保存。</span>
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="split">
      <div class="panel">
        <header class="head">
          <h3>字典类型</h3>
          <button class="btn btn-primary" :disabled="!canEdit" @click="openCreateType">新增类型</button>
        </header>
        <table>
          <thead>
            <tr><th v-if="canEdit" class="col-drag"></th><th>序号</th><th>编码</th><th>名称</th><th>状态</th><th>操作</th></tr>
          </thead>
          <tbody>
            <tr
              v-for="(t, idx) in types"
              :key="t.id"
              :class="{ active: selectedType && selectedType.id === t.id, 'row-dragging': typeDragFrom === idx, 'row-drop-target': typeDropOver === idx && typeDragFrom !== null && typeDragFrom !== idx }"
              @dragover.prevent="onTypeDragOver(idx)"
              @dragleave="onTypeDragLeave(idx)"
              @drop.prevent="onTypeDrop(idx)"
            >
              <td v-if="canEdit" class="col-drag">
                <span
                  class="drag-handle"
                  draggable="true"
                  title="拖拽排序"
                  @dragstart.stop="onTypeDragStart(idx, $event)"
                  @dragend="onTypeDragEnd"
                >⋮⋮</span>
              </td>
              <td>{{ rowNo(idx) }}</td>
              <td @click="selectType(t)">{{ t.typeCode }}</td>
              <td @click="selectType(t)">{{ t.typeName }}</td>
              <td class="col-status">
                <StatusSwitch
                  :model-value="isEnabled(t.statusCode)"
                  :disabled="!canEdit || typeStatusSavingId === t.id || typeReordering"
                  @change="(v) => onTypeStatusToggle(t, v)"
                />
              </td>
              <td>
                <button class="btn btn-edit" :disabled="!canEdit" @click.stop="openEditType(t)">编辑</button>
                <button class="btn btn-delete" :disabled="!canDelete" @click.stop="onDeleteType(t)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="panel">
        <header class="head">
          <h3>字典项{{ selectedType ? `（${selectedType.typeName}）` : '' }}</h3>
          <button class="btn btn-primary" :disabled="!selectedType || !canEdit" @click="openCreateItem">新增项</button>
        </header>
        <p v-if="!selectedType" class="muted">请先在左侧选择一个字典类型。</p>
        <table v-else>
          <thead>
            <tr><th v-if="canEdit" class="col-drag"></th><th>序号</th><th>项编码</th><th>显示名</th><th>状态</th><th>操作</th></tr>
          </thead>
          <tbody>
            <tr
              v-for="(it, idx) in items"
              :key="it.id"
              :class="{ 'row-dragging': itemDragFrom === idx, 'row-drop-target': itemDropOver === idx && itemDragFrom !== null && itemDragFrom !== idx }"
              @dragover.prevent="onItemDragOver(idx)"
              @dragleave="onItemDragLeave(idx)"
              @drop.prevent="onItemDrop(idx)"
            >
              <td v-if="canEdit" class="col-drag">
                <span
                  class="drag-handle"
                  draggable="true"
                  title="拖拽排序"
                  @dragstart.stop="onItemDragStart(idx, $event)"
                  @dragend="onItemDragEnd"
                >⋮⋮</span>
              </td>
              <td>{{ rowNo(idx) }}</td>
              <td>{{ it.itemCode }}</td>
              <td>{{ it.itemLabel }}</td>
              <td class="col-status">
                <StatusSwitch
                  :model-value="isEnabled(it.statusCode)"
                  :disabled="!canEdit || itemStatusSavingId === it.id || itemReordering"
                  @change="(v) => onItemStatusToggle(it, v)"
                />
              </td>
              <td>
                <button class="btn btn-edit" :disabled="!canEdit" @click="openEditItem(it)">编辑</button>
                <button class="btn btn-delete" :disabled="!canDelete" @click="onDeleteItem(it)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showTypeDialog" class="mask" @click.self="showTypeDialog=false">
      <div class="dialog dialog--shell" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ typeEditId ? '编辑字典类型' : '新增字典类型' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showTypeDialog=false">×</button>
        </div>
        <div class="dialog-body">
        <div class="form-grid">
          <label class="field"><span>类型编码：</span><input v-model="typeModel.typeCode" placeholder="如 PAGE_TYPE"></label>
          <label class="field"><span>类型名称：</span><input v-model="typeModel.typeName" placeholder="如 跳转类型"></label>
          <label class="field field-switch">
            <span>状态：</span>
            <StatusSwitch
              :model-value="isEnabled(typeModel.statusCode)"
              @update:model-value="(v) => typeModel.statusCode = v ? 'ENABLED' : 'DISABLED'"
            />
          </label>
          <label class="field full"><span>备注：</span><input v-model="typeModel.remark" placeholder="可空"></label>
        </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="showTypeDialog=false">取消</button>
          <button type="button" class="btn btn-primary" @click="submitType">保存</button>
        </div>
      </div>
    </div>

    <div v-if="showItemDialog" class="mask" @click.self="showItemDialog=false">
      <div class="dialog dialog--shell" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ itemEditId ? '编辑字典项' : '新增字典项' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showItemDialog=false">×</button>
        </div>
        <div class="dialog-body">
        <div class="form-grid">
          <label class="field">
            <span>所属类型：</span>
            <input :value="selectedType ? `${selectedType.typeName}（${selectedType.typeCode}）` : ''" disabled placeholder="请先在左侧选择类型">
          </label>
          <label class="field">
            <span>项编码：</span>
            <input v-model="itemModel.itemCode" placeholder="可留空，系统会按显示名称自动生成">
            <small class="muted">运营可只填「显示名称」；编码为系统识别 key，小程序固定项请勿改编码。</small>
          </label>
          <label class="field"><span>显示名称：</span><input v-model="itemModel.itemLabel" placeholder="小程序上展示的文字"></label>
          <label class="field field-switch">
            <span>状态：</span>
            <StatusSwitch
              :model-value="isEnabled(itemModel.statusCode)"
              @update:model-value="(v) => itemModel.statusCode = v ? 'ENABLED' : 'DISABLED'"
            />
          </label>
          <label class="field full"><span>备注：</span><input v-model="itemModel.remark" placeholder="可空"></label>
        </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="showItemDialog=false">取消</button>
          <button type="button" class="btn btn-primary" @click="submitItem">保存</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import {
  createDictItem, createDictType, deleteDictItem, deleteDictType,
  getDictItems, getDictTypes, reorderDictItems, reorderDictTypes,
  updateDictItem, updateDictType
} from '@/api'
import StatusSwitch from '@/components/StatusSwitch.vue'
import { hasAnyPermission } from '@/utils/adminPermission'

const emptyType = () => ({ typeCode: '', typeName: '', sortOrder: 0, statusCode: 'ENABLED', remark: '' })
const emptyItem = () => ({ typeId: null, typeCode: '', itemCode: '', itemLabel: '', sortOrder: 0, statusCode: 'ENABLED', remark: '' })

export default {
  name: 'DictManageView',
  components: { StatusSwitch },
  data() {
    return {
      types: [],
      items: [],
      selectedType: null,
      errorMsg: '',
      showTypeDialog: false,
      typeEditId: null,
      typeModel: emptyType(),
      showItemDialog: false,
      itemEditId: null,
      itemModel: emptyItem(),
      canEdit: false,
      canDelete: false,
      typeStatusSavingId: null,
      itemStatusSavingId: null,
      typeDragFrom: null,
      typeDropOver: null,
      typeReordering: false,
      itemDragFrom: null,
      itemDropOver: null,
      itemReordering: false,
    }
  },
  mounted() {
    this.canEdit = hasAnyPermission(['dict.manage', 'dict.edit'])
    this.canDelete = hasAnyPermission(['dict.manage', 'dict.delete'])
    this.loadTypes()
  },
  methods: {
    rowNo(idx) {
      return Math.max(1, Number(idx) + 1)
    },
    isEnabled(code) {
      return String(code || '').toUpperCase() === 'ENABLED'
    },
    async loadTypes() {
      try {
        const selectedId = this.selectedType && this.selectedType.id
        this.types = (await getDictTypes()) || []
        const q = String(this.$route?.query?.typeCode || '').trim()
        if (q) {
          const hit = this.types.find((t) => String(t.typeCode || '') === q)
          if (hit) await this.selectType(hit)
        } else if (selectedId != null) {
          const hit = this.types.find((t) => t.id === selectedId)
          if (hit) {
            this.selectedType = hit
            await this.loadItems()
          }
        }
      } catch (e) { this.errorMsg = e.message || '读取类型失败' }
    },
    async selectType(t) {
      this.selectedType = t
      await this.loadItems()
    },
    async loadItems() {
      if (!this.selectedType) { this.items = []; return }
      try {
        this.items = (await getDictItems(this.selectedType.id)) || []
      } catch (e) { this.errorMsg = e.message || '读取字典项失败' }
    },
    onTypeDragStart(idx, e) {
      if (!this.canEdit || this.typeReordering) return
      this.typeDragFrom = idx
      if (e && e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(idx))
      }
    },
    onTypeDragOver(idx) {
      if (!this.canEdit || this.typeDragFrom == null) return
      this.typeDropOver = idx
    },
    onTypeDragLeave(idx) {
      if (this.typeDropOver === idx) this.typeDropOver = null
    },
    onTypeDragEnd() {
      this.typeDragFrom = null
      this.typeDropOver = null
    },
    async onTypeDrop(toIdx) {
      if (!this.canEdit || this.typeDragFrom == null || this.typeDragFrom === toIdx) {
        this.onTypeDragEnd()
        return
      }
      const fromIdx = this.typeDragFrom
      const list = [...this.types]
      const [moved] = list.splice(fromIdx, 1)
      list.splice(toIdx, 0, moved)
      this.types = list
      this.onTypeDragEnd()
      await this.persistTypeOrder()
    },
    async persistTypeOrder() {
      this.typeReordering = true
      try {
        await reorderDictTypes(this.types.map((t) => t.id))
        this.types = this.types.map((t, i) => ({ ...t, sortOrder: i + 1 }))
        this.errorMsg = ''
      } catch (e) {
        this.errorMsg = e.message || '类型排序保存失败'
        await this.loadTypes()
      } finally {
        this.typeReordering = false
      }
    },
    onItemDragStart(idx, e) {
      if (!this.canEdit || this.itemReordering) return
      this.itemDragFrom = idx
      if (e && e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(idx))
      }
    },
    onItemDragOver(idx) {
      if (!this.canEdit || this.itemDragFrom == null) return
      this.itemDropOver = idx
    },
    onItemDragLeave(idx) {
      if (this.itemDropOver === idx) this.itemDropOver = null
    },
    onItemDragEnd() {
      this.itemDragFrom = null
      this.itemDropOver = null
    },
    async onItemDrop(toIdx) {
      if (!this.canEdit || !this.selectedType || this.itemDragFrom == null || this.itemDragFrom === toIdx) {
        this.onItemDragEnd()
        return
      }
      const fromIdx = this.itemDragFrom
      const list = [...this.items]
      const [moved] = list.splice(fromIdx, 1)
      list.splice(toIdx, 0, moved)
      this.items = list
      this.onItemDragEnd()
      await this.persistItemOrder()
    },
    async persistItemOrder() {
      if (!this.selectedType) return
      this.itemReordering = true
      try {
        await reorderDictItems(this.selectedType.id, this.items.map((it) => it.id))
        this.items = this.items.map((it, i) => ({ ...it, sortOrder: i + 1 }))
        this.errorMsg = ''
      } catch (e) {
        this.errorMsg = e.message || '字典项排序保存失败'
        await this.loadItems()
      } finally {
        this.itemReordering = false
      }
    },
    openCreateType() {
      this.typeEditId = null
      this.typeModel = { ...emptyType() }
      this.showTypeDialog = true
    },
    openEditType(t) {
      this.typeEditId = t.id
      this.typeModel = { ...emptyType(), ...t }
      this.showTypeDialog = true
    },
    typePayloadFrom(model) {
      return {
        typeCode: model.typeCode,
        typeName: model.typeName,
        sortOrder: model.sortOrder || 0,
        statusCode: model.statusCode || 'ENABLED',
        remark: model.remark || '',
      }
    },
    async submitType() {
      try {
        if (!this.typeModel.typeCode || !this.typeModel.typeName) throw new Error('编码与名称必填')
        const payload = this.typePayloadFrom(this.typeModel)
        if (this.typeEditId) await updateDictType(this.typeEditId, payload)
        else await createDictType(payload)
        this.showTypeDialog = false
        await this.loadTypes()
      } catch (e) { this.errorMsg = e.message || '保存失败' }
    },
    async onTypeStatusToggle(t, enabled) {
      if (!t || t.id == null || !this.canEdit) return
      const prev = t.statusCode
      const next = enabled ? 'ENABLED' : 'DISABLED'
      t.statusCode = next
      this.typeStatusSavingId = t.id
      try {
        await updateDictType(t.id, this.typePayloadFrom({ ...t, statusCode: next }))
        this.errorMsg = ''
      } catch (e) {
        t.statusCode = prev
        this.errorMsg = e.message || '切换状态失败'
      } finally {
        this.typeStatusSavingId = null
      }
    },
    async onDeleteType(t) {
      if (!await adminConfirm(`确定删除类型 ${t.typeName} 吗？`)) return
      try { await deleteDictType(t.id); await this.loadTypes(); this.selectedType = null; this.items = [] } catch (e) { this.errorMsg = e.message || '删除失败' }
    },
    openCreateItem() {
      if (!this.selectedType) return
      this.itemEditId = null
      this.itemModel = {
        ...emptyItem(),
        typeId: this.selectedType.id,
        typeCode: this.selectedType.typeCode,
      }
      this.showItemDialog = true
    },
    openEditItem(it) {
      this.itemEditId = it.id
      this.itemModel = {
        ...emptyItem(),
        ...it,
        typeId: it?.typeId != null ? Number(it.typeId) : (this.selectedType?.id ?? null),
        typeCode: it?.typeCode || this.selectedType?.typeCode || '',
      }
      this.showItemDialog = true
    },
    itemPayloadFrom(model) {
      return {
        typeId: model.typeId,
        typeCode: model.typeCode,
        itemCode: model.itemCode,
        itemLabel: model.itemLabel,
        sortOrder: model.sortOrder || 0,
        statusCode: model.statusCode || 'ENABLED',
        remark: model.remark || '',
      }
    },
    async submitItem() {
      try {
        if (!this.itemModel.typeId || !this.itemModel.itemLabel) throw new Error('类型、显示名必填')
        if (!String(this.itemModel.itemCode || '').trim()) {
          this.itemModel.itemCode = this.buildCodeFromName(this.itemModel.itemLabel)
        }
        const payload = this.itemPayloadFrom(this.itemModel)
        if (this.itemEditId) await updateDictItem(this.itemEditId, payload)
        else await createDictItem(payload)
        this.showItemDialog = false
        await this.loadItems()
      } catch (e) { this.errorMsg = e.message || '保存失败' }
    },
    async onItemStatusToggle(it, enabled) {
      if (!it || it.id == null || !this.canEdit) return
      const prev = it.statusCode
      const next = enabled ? 'ENABLED' : 'DISABLED'
      it.statusCode = next
      this.itemStatusSavingId = it.id
      try {
        await updateDictItem(it.id, this.itemPayloadFrom({ ...it, statusCode: next }))
        this.errorMsg = ''
      } catch (e) {
        it.statusCode = prev
        this.errorMsg = e.message || '切换状态失败'
      } finally {
        this.itemStatusSavingId = null
      }
    },
    async onDeleteItem(it) {
      if (!await adminConfirm(`确定删除项 ${it.itemLabel} 吗？`)) return
      try { await deleteDictItem(it.id); await this.loadItems() } catch (e) { this.errorMsg = e.message || '删除失败' }
    },
    buildCodeFromName(name) {
      const raw = String(name || '').trim().toLowerCase()
      const base = raw.replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '')
      return base || `item_${Date.now().toString(36)}`
    },
  }
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; margin-bottom: 14px; }
.tip { background: #f5f8ff; border: 1px solid #dae5ff; color: #2a4b8d; padding: 10px 12px; border-radius: 8px; margin-bottom: 10px; line-height: 1.6; font-size: 13px; }
.split { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 10px; }
.panel { border: 1px solid #eef2f7; border-radius: 12px; padding: 12px; min-height: 200px; }
.head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
h2 { margin-top: 0; }
h3 { margin: 0; font-size: 15px; color: #1d2939; }
table { width: 100%; border-collapse: collapse; } th, td { border-bottom: 1px solid #edf0f5; padding: 8px; font-size: 13px; text-align: left; }
tr.active { background: #eef4ff; }
tr.row-dragging { opacity: 0.45; }
tr.row-drop-target { background: #f0f7ff; box-shadow: inset 0 2px 0 #2f6af6; }
.muted { color: #98a2b3; font-size: 13px; }
.col-status { width: 72px; }
.col-drag { width: 34px; padding-left: 4px; padding-right: 4px; }
.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: #98a2b3;
  cursor: grab;
  user-select: none;
  letter-spacing: -2px;
  font-size: 14px;
}
.drag-handle:active { cursor: grabbing; color: #2f6af6; background: #eef4ff; }
.btn { border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn + .btn { margin-left: 8px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.error { color: #d92d20; }
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  min-height: 0;
}
.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; }
.field > span { font-weight: 600; }
.field-switch { align-items: flex-start; }
.full { grid-column: span 2; }
input, select { border: 1px solid #d0d7e2; border-radius: 8px; padding: 8px 10px; background: #fff; }
</style>
